import type { DeliveryNote } from "@shared/delivery-note";
import { fileToCompressedBase64 } from "./image-compress";
import { dispatchMissingKeyEventIfNeeded } from "./queryClient";

export async function extractDeliveryNote(invoiceId: string | number, file: File): Promise<DeliveryNote> {
  if (file.size > 15 * 1024 * 1024) throw new Error("Der Lieferschein darf höchstens 15 MB groß sein.");
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  if (!isPdf && !["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
    throw new Error("Bitte ein PDF oder ein JPEG-, PNG- oder WebP-Foto auswählen.");
  }
  const image = isPdf ? null : await fileToCompressedBase64(file);
  const data = image?.data ?? await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("Lieferschein konnte nicht gelesen werden."));
    reader.readAsDataURL(file);
  });
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 180000);
  try {
    const response = await fetch(`/api/invoices/${invoiceId}/delivery-notes/extract`, {
      method: "POST", credentials: "include", signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileName: file.name, mimeType: isPdf ? "application/pdf" : image!.mimeType, data }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      dispatchMissingKeyEventIfNeeded(response.status, result.message ?? "");
      throw new Error(result.message || "Lieferschein konnte nicht ausgewertet werden.");
    }
    return result;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Die Erkennung hat zu lange gedauert. Bitte den Lieferschein in kleinere Dateien aufteilen und erneut versuchen.");
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}
